import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Mail, RefreshCw, Search, Eye, MousePointerClick, ChevronLeft, ChevronRight, X,
  ArrowUp, ArrowDown, ArrowUpDown, FileText,
} from "lucide-react";

const SortIcon = ({ field, sortBy, sortOrder }) => {
  const isActive = sortBy === field;
  if (!isActive) return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
  return sortOrder === "asc"
    ? <ArrowUp className="w-3 h-3 text-indigo-600" />
    : <ArrowDown className="w-3 h-3 text-indigo-600" />;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => {
  if (status === "delivered") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">delivered</Badge>;
  if (status === "sent") return <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">sent</Badge>;
  if (status === "delayed") return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">delayed</Badge>;
  if (status === "bounced") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">bounced</Badge>;
  if (status === "complained") return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">spam complaint</Badge>;
  if (status === "failed") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">failed</Badge>;
  return <Badge variant="secondary">{status || "pending"}</Badge>;
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "delayed", label: "Delayed" },
  { value: "bounced", label: "Bounced" },
  { value: "complained", label: "Spam complaint" },
  { value: "failed", label: "Failed" },
];

const ENGAGEMENT_OPTIONS = [
  { value: "any", label: "Any engagement" },
  { value: "opened", label: "Opened" },
  { value: "not_opened", label: "Not opened" },
  { value: "clicked", label: "Clicked" },
  { value: "not_clicked", label: "Not clicked" },
];

const TYPE_OPTIONS = [
  { value: "any", label: "All sends" },
  { value: "oneoff", label: "One-off sends" },
  { value: "sequence", label: "Sequence sends" },
  { value: "test", label: "Test sends" },
];

const PAGE_SIZES = [25, 50, 100, 200];

const SentItems = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [sequences, setSequences] = useState([]);

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [engagement, setEngagement] = useState("any");
  const [typeFilter, setTypeFilter] = useState("any");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1); // 1-indexed for the UI

  // Sorting
  const [sortBy, setSortBy] = useState("sent_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc"); // default to "most" for engagement columns
    }
  };

  // Timeline modal (same as Email Tools)
  const [timelineDialog, setTimelineDialog] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (templateId) params.template_id = templateId;
      if (sequenceId) params.sequence_id = sequenceId;
      if (engagement === "opened") params.opened = true;
      else if (engagement === "not_opened") params.opened = false;
      else if (engagement === "clicked") params.clicked = true;
      else if (engagement === "not_clicked") params.clicked = false;
      if (typeFilter && typeFilter !== "any") params.origin = typeFilter;

      const res = await axios.get(`${API}/email-sends`, {
        params, withCredentials: true, headers: authHeaders,
      });
      setRows(res.data.sends || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load sent items"));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, q, status, templateId, sequenceId, engagement, typeFilter, page, pageSize, sortBy, sortOrder]);

  // Fetch dropdown options once on mount
  useEffect(() => {
    (async () => {
      try {
        const [tplRes, seqRes] = await Promise.all([
          axios.get(`${API}/email-templates`, { withCredentials: true, headers: authHeaders }),
          axios.get(`${API}/sequences`, { withCredentials: true, headers: authHeaders }),
        ]);
        setTemplates(tplRes.data.templates || []);
        setSequences(seqRes.data.sequences || []);
      } catch (err) { /* silent */ }
    })();
  }, [authHeaders]);

  // Re-fetch when filters/page change
  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Reset to page 1 when filters change (but not when page itself changes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [q, status, templateId, sequenceId, engagement, typeFilter, pageSize]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleOpenTimeline = async (sendRow) => {
    setTimelineDialog({ sendRow, events: [], meta: null, loading: true });
    try {
      const res = await axios.get(`${API}/email-sends/${sendRow.id}/events`, {
        withCredentials: true, headers: authHeaders,
      });
      setTimelineDialog({ sendRow, events: res.data.events || [], meta: res.data.meta || null, loading: false });
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load timeline"));
      setTimelineDialog(null);
    }
  };

  const clearFilters = () => {
    setQ(""); setStatus(""); setTemplateId(""); setSequenceId(""); setEngagement("any"); setTypeFilter("any");
  };
  const hasActiveFilters = q || status || templateId || sequenceId || engagement !== "any" || typeFilter !== "any";

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sent-items-page">
      <Sidebar user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Sent Items
            </h1>
            <p className="text-slate-500 mt-1">
              Every email Storm Pad has sent. Click any status badge for the full delivery timeline.
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-base">
              <span>Filters</span>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X className="w-4 h-4 mr-1" /> Clear filters
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Email, name, or subject…"
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={status || "any"} onValueChange={(v) => setStatus(v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value || "any"} value={o.value || "any"}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Template</Label>
                <Select value={templateId || "any"} onValueChange={(v) => setTemplateId(v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All templates" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">All templates</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Sequence</Label>
                <Select value={sequenceId || "any"} onValueChange={(v) => setSequenceId(v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All sends" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">All sends</SelectItem>
                    {sequences.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Engagement</Label>
                <Select value={engagement} onValueChange={setEngagement}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Sent emails</span>
              <Button variant="ghost" size="sm" onClick={fetchRows} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </CardTitle>
            <CardDescription>
              {loading ? "Loading…" : (
                total === 0
                  ? "No matching sends"
                  : `Showing ${showingFrom}–${showingTo} of ${total}`
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-slate-500 text-sm">No matching sends. Try clearing some filters.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Sequence</TableHead>
                    <TableHead>
                      <button type="button" onClick={() => handleSort("sent_at")} className="inline-flex items-center gap-1 hover:text-slate-900">
                        Sent <SortIcon field="sent_at" sortBy={sortBy} sortOrder={sortOrder} />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" onClick={() => handleSort("status")} className="inline-flex items-center gap-1 hover:text-slate-900">
                        Status <SortIcon field="status" sortBy={sortBy} sortOrder={sortOrder} />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button type="button" onClick={() => handleSort("open_count")} className="inline-flex items-center gap-1 hover:text-slate-900">
                        Opens <SortIcon field="open_count" sortBy={sortBy} sortOrder={sortOrder} />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button type="button" onClick={() => handleSort("click_count")} className="inline-flex items-center gap-1 hover:text-slate-900">
                        Clicks <SortIcon field="click_count" sortBy={sortBy} sortOrder={sortOrder} />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => {
                    const isFailed = t.status === "failed" || t.status === "bounced" || t.status === "complained";
                    return (
                      <TableRow key={t.id} className={isFailed ? "bg-red-50/50" : ""}>
                        <TableCell>
                          <div className="font-mono text-xs">{t.recipient_email}</div>
                          {t.recipient_name && <div className="text-xs text-slate-500">{t.recipient_name}</div>}
                        </TableCell>
                        <TableCell>
                          {t.template_name ? (
                            <span className="text-sm">
                              {t.template_name}
                              {t.category && <Badge variant="outline" className="ml-1 text-xs">{t.category}</Badge>}
                              {t.is_test && <Badge variant="outline" className="ml-1 text-xs text-amber-700 border-amber-300">test</Badge>}
                            </span>
                          ) : <span className="text-slate-400 text-sm">—</span>}
                        </TableCell>
                        <TableCell>
                          {t.sequence_id ? (() => {
                            // Prefer the snapshot name on the send row (set at send time so deletes
                            // don't break the display); fall back to looking up the live sequence;
                            // if neither resolves, the source sequence has been deleted.
                            const snapshot = (t.sequence_name || "").trim();
                            const live = (sequences.find((s) => s.id === t.sequence_id) || {}).name;
                            const label = snapshot || live;
                            return (
                              <span className="text-xs">
                                {label
                                  ? label
                                  : <span className="italic text-slate-400">Deleted sequence</span>}
                                {t.step_index != null && <span className="text-slate-500"> · step {t.step_index + 1}</span>}
                              </span>
                            );
                          })() : <span className="text-slate-400 text-sm">—</span>}
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs">{formatDate(t.sent_at)}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => handleOpenTimeline(t)}
                            className="hover:opacity-80 cursor-pointer"
                            title="Click for full delivery timeline"
                          >
                            <StatusBadge status={t.status} />
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          {t.first_opened_at ? (
                            <span
                              className="inline-flex items-center gap-1 text-blue-600"
                              title={`Last opened ${new Date(t.last_opened_at || t.first_opened_at).toLocaleString()}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span className="text-sm font-medium">{t.open_count || 1}</span>
                            </span>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {t.first_clicked_at ? (
                            <span
                              className="inline-flex items-center gap-1 text-violet-600"
                              title={`Last clicked ${new Date(t.last_clicked_at || t.first_clicked_at).toLocaleString()}${t.last_clicked_url ? `\nURL: ${t.last_clicked_url}` : ""}`}
                            >
                              <MousePointerClick className="w-3.5 h-3.5" />
                              <span className="text-sm font-medium">{t.click_count || 1}</span>
                            </span>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {total > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Rows per page:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                    <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Timeline modal */}
      <Dialog open={!!timelineDialog} onOpenChange={(open) => !open && setTimelineDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delivery timeline</DialogTitle>
            <DialogDescription>
              {timelineDialog?.sendRow?.recipient_email}{" "}
              {timelineDialog?.sendRow?.subject && (
                <span className="text-slate-500">— {timelineDialog.sendRow.subject}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {timelineDialog?.meta && !timelineDialog.loading && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
              {timelineDialog.meta.template_name && (
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 w-20 flex-shrink-0">Template</span>
                  <span className="text-slate-900 font-medium">{timelineDialog.meta.template_name}</span>
                  {timelineDialog.meta.category && (
                    <Badge variant="outline" className="text-xs">{timelineDialog.meta.category}</Badge>
                  )}
                  {timelineDialog.meta.is_test && (
                    <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">test</Badge>
                  )}
                </div>
              )}
              {timelineDialog.meta.sequence && (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-slate-500 w-20 flex-shrink-0">Sequence</span>
                    {timelineDialog.meta.sequence.sequence_name ? (
                      <span className="text-slate-900 font-medium">
                        {timelineDialog.meta.sequence.sequence_name}
                        {timelineDialog.meta.sequence.deleted && (
                          <span className="ml-2 italic text-slate-400 text-xs">(deleted)</span>
                        )}
                      </span>
                    ) : (
                      <span className="italic text-slate-400">Deleted sequence</span>
                    )}
                  </div>
                  {timelineDialog.meta.sequence.step_position != null && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-slate-500 w-20 flex-shrink-0">Step</span>
                      <span className="text-slate-900">
                        {timelineDialog.meta.sequence.step_position}
                        {timelineDialog.meta.sequence.total_steps ? ` of ${timelineDialog.meta.sequence.total_steps}` : ""}
                      </span>
                    </div>
                  )}
                </>
              )}
              {timelineDialog.meta.from_address && (
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 w-20 flex-shrink-0">From</span>
                  <span className="text-slate-700 font-mono">{timelineDialog.meta.from_address}</span>
                </div>
              )}
              {timelineDialog.meta.hubspot_contact_id && (
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 w-20 flex-shrink-0">HubSpot ID</span>
                  <span className="text-slate-700 font-mono">{timelineDialog.meta.hubspot_contact_id}</span>
                </div>
              )}
            </div>
          )}
          {timelineDialog?.loading ? (
            <p className="text-slate-500 text-sm py-4">Loading…</p>
          ) : (timelineDialog?.events || []).length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No events recorded.</p>
          ) : (
            <ol className="relative border-l border-slate-200 ml-3 space-y-5 py-2">
              {(timelineDialog?.events || []).map((e, idx) => {
                const colorMap = {
                  sent: "bg-emerald-100 text-emerald-700 ring-emerald-100",
                  delivered: "bg-emerald-200 text-emerald-800 ring-emerald-200",
                  delivery_delayed: "bg-amber-100 text-amber-700 ring-amber-100",
                  opened: "bg-blue-100 text-blue-700 ring-blue-100",
                  clicked: "bg-violet-100 text-violet-700 ring-violet-100",
                  bounced: "bg-red-100 text-red-700 ring-red-100",
                  complained: "bg-orange-100 text-orange-700 ring-orange-100",
                  send_failed: "bg-red-100 text-red-700 ring-red-100",
                };
                const labelMap = {
                  sent: "Sent",
                  send_failed: "Send failed",
                  delivered: "Delivered",
                  delivery_delayed: "Delivery delayed",
                  opened: "Opened",
                  clicked: "Clicked",
                  bounced: "Bounced",
                  complained: "Spam complaint",
                };
                return (
                  <li key={idx} className="ml-4">
                    <span className={`absolute -left-1.5 w-3 h-3 rounded-full ring-4 ${colorMap[e.type] || "bg-slate-200 text-slate-700 ring-slate-100"}`}></span>
                    <div className="flex items-baseline justify-between gap-3">
                      <Badge className={`${colorMap[e.type] || "bg-slate-100 text-slate-700"} hover:${colorMap[e.type] || "bg-slate-100 text-slate-700"}`}>
                        {labelMap[e.type] || e.type}
                      </Badge>
                      <span className="text-xs text-slate-500 font-mono">
                        {e.at ? new Date(e.at).toLocaleString() : "—"}
                      </span>
                    </div>
                    {e.detail && (
                      <p className="text-xs text-slate-600 mt-1 break-words">{e.detail}</p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          <DialogFooter className="gap-2">
            {timelineDialog?.meta && (timelineDialog.meta.rendered_html || timelineDialog.meta.rendered_text) && (
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              >
                <FileText className="w-4 h-4 mr-2" /> View email
              </Button>
            )}
            <Button variant="outline" onClick={() => setTimelineDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email content preview sub-dialog. Shows the rendered HTML exactly as
          Resend received it. Sandboxed iframe so any inline styles render
          faithfully without giving the email JS access to the parent page. */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
            <DialogDescription>
              {timelineDialog?.meta?.subject && (
                <span className="text-slate-700">{timelineDialog.meta.subject}</span>
              )}
              <br />
              <span className="text-xs text-slate-500">
                Sent to {timelineDialog?.sendRow?.recipient_email}
                {timelineDialog?.sendRow?.sent_at && <> · {formatDate(timelineDialog.sendRow.sent_at)}</>}
              </span>
            </DialogDescription>
          </DialogHeader>
          {timelineDialog?.meta?.rendered_html ? (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={timelineDialog.meta.rendered_html}
              className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
            />
          ) : timelineDialog?.meta?.rendered_text ? (
            <pre className="w-full max-h-[60vh] overflow-auto whitespace-pre-wrap text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3">
              {timelineDialog.meta.rendered_text}
            </pre>
          ) : (
            <p className="text-sm text-slate-500 py-6 text-center">
              No saved copy of this email's content. Preview is only available for sends made after the snapshot feature was enabled.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SentItems;
